import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoLayers } from 'react-icons/io5';
import { BsLayersFill } from 'react-icons/bs';
import { PuzzleIcon } from 'lucide-react';
import { HiArrowRight, HiCheck, HiArrowLeft } from 'react-icons/hi';
import CustomButton from '../../../components/common/CustomButton';
import { FaChevronCircleLeft } from 'react-icons/fa';

// ... (existing code)


interface AboutWorkItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = [
  {
    id: "intro",
    title: "About Cadence Pro Work Items",
    description:
      "Get familiar with work item types to organize your projects more effectively",
    icon: null,
    color: "",
    flow: null,
  },
  {
    id: "normal_epic",
    title: "Normal Epic",
    description: "Best for high-level planning to group tasks under an epic",
    icon: BsLayersFill,
    color: "text-violet-400",
    flow: "Epic → Task",
  },
  {
    id: "advanced_epic",
    title: "Advanced Epic",
    description: "For complex project planning with multiple features",
    icon: IoLayers,
    color: "text-violet-400",
    flow: "Epic → Feature → Task",
  },
  {
    id: "feature",
    title: "Feature",
    description: "For focused, incremental feature work",
    icon: PuzzleIcon,
    color: "text-sky-400",
    flow: "Feature → Task",
  },
];

const AboutWorkItemsModal: React.FC<AboutWorkItemsModalProps> = ({ isOpen, onClose }) => {

  const [currentStep, setCurrentStep] = useState(0);

  // Reset step when opened
  useEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col min-h-[460px]"
      >
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center w-full"
            >
              {/* Icon / Viz */}
              <div className="mb-8 relative h-32 flex items-center justify-center">
                {currentStep === 0 ? (
                  /* Intro Spinner Logo */
                  <div className="relative">
                    <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-blue-500/10 dark:bg-blue-500/10 rounded-full blur-[40px] pointer-events-none" />
                    <motion.img
                      src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
                      className="h-24 w-24 object-contain"
                      alt="Loading..."
                      animate={{ rotate: 360 }}
                      transition={{
                        repeat: Infinity,
                        duration: 10,
                        ease: "linear",
                      }}
                    />
                  </div>
                ) : (
                  /* Step Icon */
                  <div className="relative">
                    <div
                      className={`absolute top-[-50%] left-[-50%] w-[200%] h-[200%] rounded-full blur-[40px] pointer-events-none ${
                        steps[currentStep].color.includes("sky")
                          ? "bg-sky-500/10"
                          : "bg-violet-500/10"
                      }`}
                    />
                    <div className="p-6 bg-gray-50 dark:bg-slate-800/50 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm relative z-10">
                      {React.createElement(steps[currentStep].icon as any, {
                        className: `text-5xl ${steps[currentStep].color}`,
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {steps[currentStep].title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 max-w-sm text-sm">
                {steps[currentStep].description}
              </p>

              {/* Flow Pill (if applicable) */}
              {steps[currentStep].flow && (
                <div className="px-4 py-1.5 bg-gray-100 dark:bg-slate-800 rounded-full text-xs font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-slate-700 mb-2">
                  {steps[currentStep].flow}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="relative p-6 border-t border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/50 ">
          {currentStep > 0 && (
            <div className="flex">
              <CustomButton
                variant="secondary"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <HiArrowLeft className="text-lg" />
              </CustomButton>
            </div>
          )}
          <div className="flex gap-1.5 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep
                    ? "w-6 bg-blue-600 dark:bg-blue-500"
                    : "w-1.5 bg-gray-200 dark:bg-slate-700"
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2 w-full">
            <CustomButton
              onClick={handleNext}
              className="flex items-center gap-2 ml-auto "
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Get Started <HiCheck className="text-lg" />
                </>
              ) : (
                <>
                  Next <HiArrowRight className="text-lg" />
                </>
              )}
            </CustomButton>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AboutWorkItemsModal;
